/**
 * App Bridge utility functions
 * Handles App Bridge session token management
 * 
 * According to Shopify docs: https://shopify.dev/docs/api/app-bridge-library
 * App Bridge reads the API key from the meta tag with name="shopify-api-key"
 * After loading the CDN script, you have access to the `shopify` global variable.
 */

/**
 * Get API key from query parameters or environment variables
 * Priority: query param > environment variable
 */
function getApiKey(): string | null {
  // First, try to get from query parameters
  const urlParams = new URLSearchParams(window.location.search);
  const apiKeyFromQuery = urlParams.get('api_key');
  if (apiKeyFromQuery) {
    return apiKeyFromQuery;
  }

  // Fallback to environment variable
  const apiKeyFromEnv = import.meta.env.VITE_SHOPIFY_API_KEY;
  if (apiKeyFromEnv) {
    return apiKeyFromEnv;
  }

  return null;
}

/**
 * Configure App Bridge API key
 * Updates the meta tag so App Bridge can read it
 */
function configureApiKey(): string | null {
  const apiKey = getApiKey();
  if (!apiKey) {
    console.warn(
      'App Bridge: API key not found in query params (api_key) or environment (VITE_SHOPIFY_API_KEY)',
    );
    return null;
  }

  // Update the meta tag so App Bridge can read it
  let metaTag = document.querySelector('meta[name="shopify-api-key"]') as HTMLMetaElement;
  if (!metaTag) {
    // Create meta tag if it doesn't exist
    metaTag = document.createElement('meta');
    metaTag.name = 'shopify-api-key';
    document.head.appendChild(metaTag);
  }
  metaTag.content = apiKey;

  return apiKey;
}

/**
 * Get the shopify global variable
 * This is available after the CDN script loads
 * Also ensures API key is configured
 */
export function getShopify(): any {
  if (typeof window === 'undefined') {
    return null;
  }

  // Ensure API key is configured
  configureApiKey();

  // Check if shopify global variable is available
  const shopify = (window as any).shopify;
  if (!shopify) {
    console.warn(
      'App Bridge: shopify global variable not found. Ensure the script is loaded from CDN in index.html',
    );
    return null;
  }

  return shopify;
}

/**
 * Get App Bridge instance (for backward compatibility)
 * Note: Modern App Bridge uses the shopify global variable directly
 */
export function getAppBridge(): any {
  return getShopify();
}

/**
 * Get session token for authenticated API requests
 * Returns null if App Bridge is not available or token cannot be obtained
 */
export async function getSessionToken(): Promise<string | null> {
  const shopify = getShopify();
  if (!shopify) {
    console.warn('App Bridge: shopify object not available');
    return null;
  }

  try {
    // First, try to get id_token from URL query params (fastest, already available)
    const urlParams = new URLSearchParams(window.location.search);
    const idTokenFromUrl = urlParams.get('id_token');
    if (idTokenFromUrl) {
      console.log('Using id_token from URL query params');
      return idTokenFromUrl;
    }

    // Add a unified timeout for both ready() and idToken()
    const createTimeout = (ms: number, message: string) => 
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error(message)), ms));

    // Wait for App Bridge to be ready if it has a ready property
    if (shopify.ready && typeof shopify.ready === 'function') {
      console.log('Waiting for App Bridge to be ready...');
      await Promise.race([
        shopify.ready(),
        createTimeout(5000, 'shopify.ready() call timed out after 5 seconds')
      ]);
      console.log('App Bridge is ready');
    } else if (shopify.ready && typeof shopify.ready.then === 'function') {
      // ready might be a promise
      await Promise.race([
        shopify.ready,
        createTimeout(5000, 'shopify.ready promise timed out after 5 seconds')
      ]);
    }

    // Use shopify.idToken() to get the ID token
    if (typeof shopify.idToken === 'function') {
      console.log('Calling shopify.idToken()...');
      
      const idTokenPromise = shopify.idToken();
      const idToken = await Promise.race([
        idTokenPromise, 
        createTimeout(5000, 'idToken() call timed out after 5 seconds')
      ]);
      
      console.log('idToken received:', idToken ? 'received' : 'null');
      return idToken;
    } else {
      console.warn('App Bridge: idToken method not found on shopify object');
      return null;
    }
  } catch (error) {
    console.error('Failed to get session token:', error);
    
    // Fallback: try to get from URL again (in case it was added dynamically)
    const urlParams = new URLSearchParams(window.location.search);
    const idTokenFromUrl = urlParams.get('id_token');
    if (idTokenFromUrl) {
      console.log('Using id_token from URL as fallback');
      return idTokenFromUrl;
    }
    
    return null;
  }
}

/**
 * Check if App Bridge is available
 */
export function isAppBridgeAvailable(): boolean {
  return getShopify() !== null;
}

/**
 * App Bridge Actions - Helper functions for common actions
 */

/**
 * Show a toast notification
 */
export function showToast(message: string, options?: { duration?: number }) {
  const shopify = getShopify();
  if (!shopify) {
    console.warn('App Bridge toast not available');
    return;
  }

  try {
    // Use shopify global variable for toast
    if (shopify.toast && typeof shopify.toast.show === 'function') {
      shopify.toast.show(message, options?.duration || 5000);
    } else {
      console.warn('App Bridge: toast.show method not found');
    }
  } catch (error) {
    console.error('Failed to show toast:', error);
  }
}

/**
 * Show a modal dialog
 */
export function showModal(title: string, message: string) {
  const shopify = getShopify();
  if (!shopify) {
    console.warn('App Bridge modal not available');
    return;
  }

  try {
    // Use shopify global variable for modal
    if (shopify.modal && typeof shopify.modal.show === 'function') {
      shopify.modal.show({
        title,
        message,
      });
    } else {
      console.warn('App Bridge: modal.show method not found');
    }
  } catch (error) {
    console.error('Failed to show modal:', error);
  }
}

/**
 * Navigate to a different page in the app
 */
export function navigateTo(path: string) {
  const shopify = getShopify();
  if (!shopify) {
    // Fallback to regular navigation if App Bridge not available
    window.location.href = path;
    return;
  }

  try {
    // Use shopify global variable for navigation
    if (shopify.navigate && typeof shopify.navigate === 'function') {
      shopify.navigate(path);
    } else if (shopify.dispatch && typeof shopify.dispatch === 'function') {
      shopify.dispatch({
        type: 'APP::NAVIGATION::REDIRECT',
        payload: { path },
      });
    } else {
      console.warn('App Bridge: navigation method not found, using fallback');
      window.location.href = path;
    }
  } catch (error) {
    console.error('Failed to navigate:', error);
    // Fallback to regular navigation
    window.location.href = path;
  }
}

